"use client";

import { useMemo, useState, useTransition } from "react";
import { createForumThreadAction } from "@/lib/forum-actions";

const PRESETS = [
  { label: "Meccs", flair: "MATCH", type: "MATCH", body: "Meccsnapos reakciók, kulcspillanatok, kezdőcsapat és élő benyomások." },
  { label: "Transfer", flair: "TRANSFER", type: "TRANSFER", body: "Forrás, valószínűség, ár és taktikai fit. Mi ebben a legérdekesebb?" },
  { label: "Question", flair: "GENERAL", type: "DISCUSSION", body: "Egy gyors kérdés a közösséghez: hogyan látjátok ezt?" },
  { label: "Meme", flair: "MEME", type: "DISCUSSION", body: "Könnyedebb thread, de maradjon témán és legyen érthető a context." },
  { label: "Analysis", flair: "ANALYSIS", type: "DISCUSSION", body: "Hosszabb take, taktika, forma, trend vagy adat-alapú olvasat." },
] as const;

function suggestFlair(title: string, body: string) {
  const text = `${title} ${body}`.toLowerCase();
  if (text.includes("transfer") || text.includes("igazol")) return "TRANSFER";
  if (text.includes("meme")) return "MEME";
  if (text.includes("miert") || text.includes("miért") || text.includes("?")) return "GENERAL";
  if (text.includes("analysis") || text.includes("elemz") || text.includes("taktik")) return "ANALYSIS";
  if (text.includes("match") || text.includes("meccs") || text.includes("lineup") || text.includes("kezdo")) return "MATCH";
  return "GENERAL";
}

export function ForumThreadComposer({
  forumId,
  submitLabel,
  titlePlaceholder,
  bodyPlaceholder,
  recentTitles,
}: {
  forumId: string;
  submitLabel: string;
  titlePlaceholder: string;
  bodyPlaceholder: string;
  recentTitles: string[];
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const selectedPresetConfig = useMemo(
    () => PRESETS.find((preset) => preset.label === selectedPreset) ?? null,
    [selectedPreset],
  );
  const suggestedFlair = suggestFlair(title, body);
  const flair = selectedPresetConfig?.flair ?? suggestedFlair;
  const threadType =
    selectedPresetConfig?.type ?? (suggestedFlair === "MATCH" ? "MATCH" : suggestedFlair === "TRANSFER" ? "TRANSFER" : "DISCUSSION");

  const duplicateHint = useMemo(() => {
    const normalized = title.trim().toLowerCase();
    if (normalized.length < 6) return null;
    return recentTitles.find((item) => item.toLowerCase().includes(normalized) || normalized.includes(item.toLowerCase())) ?? null;
  }, [recentTitles, title]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        setError(null);
        startTransition(async () => {
          try {
            const result = await createForumThreadAction(formData);
            if (result && !result.ok) {
              setError(result.message);
            }
          } catch (submitError) {
            console.error("[forum-ui] thread create failed", submitError);
            setError("A thread létrehozása most nem sikerült. Próbáld újra később.");
          }
        });
      }}
      className="mt-4 grid gap-4"
    >
      <input type="hidden" name="forumId" value={forumId} />
      <input type="hidden" name="flair" value={flair} />
      <input type="hidden" name="type" value={threadType} />

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => {
              setSelectedPreset(preset.label);
              setBody((value) => value || preset.body);
            }}
            className={`rounded-full px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition ${
              selectedPreset === preset.label ? "bg-[#A4243B] text-white" : "border border-[#D8C99B]/70 bg-[#FCFBF8] text-[#273E47] hover:border-[#A4243B]"
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="rounded-[20px] border border-[#D8C99B]/70 bg-[#D8C99B]/20 px-4 py-3">
        <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#273E47]/65">Ajánlott flair</p>
        <p className="mt-1 text-sm font-black text-[#273E47]">{flair}</p>
      </div>

      <div className="grid gap-2">
        <input
          name="title"
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          placeholder={titlePlaceholder}
          className="h-12 rounded-[20px] border border-[#D8C99B]/70 bg-[#FCFBF8] px-4 text-sm text-[#273E47] outline-none transition focus:border-[#A4243B] focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
          required
          minLength={6}
        />
        {duplicateHint ? (
          <p className="text-xs font-semibold text-[#A4243B]">
            Hasonló thread már fut: “{duplicateHint}”. Lehet, hogy érdemes inkább ahhoz csatlakozni.
          </p>
        ) : null}
      </div>

      <textarea
        name="body"
        value={body}
        onChange={(event) => setBody(event.currentTarget.value)}
        placeholder={bodyPlaceholder}
        className="min-h-32 rounded-[20px] border border-[#D8C99B]/70 bg-[#FCFBF8] px-4 py-4 text-sm text-[#273E47] outline-none transition focus:border-[#A4243B] focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
        required
        minLength={8}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-semibold text-[#273E47]/65">
          Röviden: legyen egyértelmű cím, egy erős nyitógondolat, és ha átigazolásról van szó, jöjjön forrás is.
        </p>
        {error ? <p className="basis-full rounded-[16px] bg-[#A4243B]/10 px-3 py-2 text-sm font-semibold text-[#A4243B]" role="alert">{error}</p> : null}
        <button disabled={pending} className="rounded-full bg-[#273E47] px-5 py-3 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:cursor-wait disabled:opacity-60">
          {pending ? "Küldés..." : submitLabel}
        </button>
      </div>
    </form>
  );
}
